// AI Generated Component
import { Component, OnInit } from '@angular/core';
import { StateService } from '@uirouter/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { DateRangeService } from '@client/app/services/date-range/date-range.service';
import { ParamService } from '@monsido/ng2/core/param/param.service';
import { StatsRepoService } from '@client/app/services/api/stats-repo/stats-repo.service';
import {
    StatisticsDateComparisonService,
} from '@monsido/modules/statistics/services/date-comparison/statistics-date-comparison.service';
import { MonModalService } from 'app/modules/modals/services/mon-modal/mon-modal.service';
import moment from 'moment';
import { MAX_PAGE_SIZE_FOR_COMPARISON, STATISTICS } from '@client/app/shared-modules/statistics/statistics.constant';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { BaseContainerComponentController } from '@client/app/blocks/base/base.controller';
import { StatisticsExportService } from '@client/app/pages/statistics/services/statistics-export.service';
import { StatisticsExportBuilderService } from '@monsido/modules/statistics/services/export/statistics-export-builder.service';

@Component({
    selector: 'statistics-pages-without-visits',
    standalone: false,
    templateUrl: './without-visits.component.html',
})
export class StatisticsPagesWithoutVisitsComponent extends BaseContainerComponentController implements OnInit {
    loading: boolean = false;
    comparisonEnabled: boolean | null = null;
    compareDate: any = { startDate: null, endDate: null };
    noPagesResultMessage: string = '';

    domain: any;
    date: any;
    dateFormat: string = '';
    override search: string = '';
    exports: any[] = [];
    pages: any = [];
    promise: any;
    summary: any = {};
    totalVisits: any;
    pageUrls: any = [];

    constructor (
        private statsRepoService: StatsRepoService,
        private stateService: StateService,
        private paramService: ParamService,
        private translateService: TranslateService,
        private dateRangeService: DateRangeService,
        private statisticsDateComparisonService: StatisticsDateComparisonService,
        private monModalService: MonModalService,
        private statisticsExportService: StatisticsExportService,
        private statisticsExportBuilderService: StatisticsExportBuilderService,
    ) {
        super({ table: true, labels: true, pages: true });
    }

    ngOnInit () {
        this.init();
    }

    init () {
        this.domain = this.sessionService.domain;
        this.comparisonEnabled = null;
        this.compareDate = { startDate: null, endDate: null };
        this.search = '';

        this.noPagesResultMessage = this.translateService.getString('No pages found');

        this.date = this.dateRangeService.setRangeDates();
        this.dateFormat = 'ddd, D. MMM YYYY';
        this.loading = false;
        this.exports = this.statisticsExportService.getPageWithoutVisitsExportDefinitions(
            this.date.startDate,
            this.date.endDate,
        );
        this.getPage();
    }

    validateDate = this.statisticsDateComparisonService.validateDate;

    override onSearch (search: string) {
        this.search = search;
        this.page = 1;
        if (this.comparisonEnabled && this.date && this.compareDate) {
            this.promise = this.onComparisonRangeSelected(
                this.date.startDate,
                this.date.endDate,
                this.compareDate.startDate,
                this.compareDate.endDate,
            );
        } else {
            this.getPage();
        }
    }

    override getPage () {
        const params = {
            limit: this.pageSize,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            from: this.date.startDate?.format(STATISTICS.DATE_FORMAT),
            to: this.date.endDate?.format(STATISTICS.DATE_FORMAT),
            search: this.search,
        };

        this.loading = true;

        this.promise = this.statsRepoService.getPagesWithoutVisits(params, contextTokens.NO_PARSE_SEARCH)
            .then((data) => {
                this.pages = data;
                (this.pages as any).currentPage = this.page;
                this.totalVisits = this.pages.reduce((total, page) => total + parseInt(page.visits), 0);
                this.pages.sort((a, b) => b.visits - a.visits);
                this.pageUrls = this.pages.map(({ url }) => url);
                this.setNoResultMessage();
            }, () => {})
            .finally(() => {
                this.loading = false;
            });
    }

    async onComparisonRangeSelected (currentStartDate: any, currentEndDate: any, compareStartDate: any, compareEndDate: any) {
        this.loading = true;

        const currentDataRequestParams = {
            limit: MAX_PAGE_SIZE_FOR_COMPARISON,
            page: this.page || 1,
            domainId: this.stateService.params.domainId,
            from: currentStartDate?.format(STATISTICS.DATE_FORMAT),
            to: currentEndDate?.format(STATISTICS.DATE_FORMAT),
            search: this.search,
        };

        const compareDataRequestParams = {
            domainId: this.stateService.params.domainId,
            from: compareStartDate?.format(STATISTICS.DATE_FORMAT),
            to: compareEndDate?.format(STATISTICS.DATE_FORMAT),
        };

        const currentPages = await this.statsRepoService.getPagesWithoutVisits(
            currentDataRequestParams,
            contextTokens.NO_PARSE_SEARCH,
        );

        let comparePages: any = [];
        if (currentPages.length) {
            comparePages = await this.statsRepoService.getPagesByUrl(
                { ...compareDataRequestParams, urls: currentPages.map(({ url }) => url) },
            );
        }

        const currentPagesMap = new Map();

        for (let i = 0; i < currentPages.length; i++) {
            const page: any = currentPages[i];
            const computedPage = {
                ...page,
                compare: {
                    visits: page.compare?.visits || 0,
                },
            };

            currentPagesMap.set(page.url, computedPage);
        }

        for (let i = 0; i < comparePages.length; i++) {
            const comparePage = comparePages[i];
            const existedPageInMap = currentPagesMap.get(comparePage.url);

            if (existedPageInMap) {
                existedPageInMap.compare = comparePage;
            }
        }

        this.pages = Array.from(currentPagesMap.values());
        this.pages.currentPage = currentPages.currentPage;
        this.pages.pageHits = (currentPages as any).pageHits;
        this.pages.perPage = currentPages.perPage;
        this.pages.total = currentPages.total;
        this.setNoResultMessage();

        this.loading = false;
    }

    setNoResultMessage () {
        const noPagesMessage = this.translateService.getString('No pages found');
        const noPagesSuggestionMessage = this.translateService.getString(
            'No pages were found. Try searching for the page URL instead of the page title.',
        );

        if (!this.pages.length && !this.search) {
            this.noPagesResultMessage = noPagesMessage;
        }

        if (!this.pages.length && this.search) {
            this.noPagesResultMessage = noPagesSuggestionMessage;
        }
    }

    onPageChangeLocal (page: number) {
        this.page = page;
        if (this.comparisonEnabled && this.date && this.compareDate) {
            this.promise = this.onComparisonRangeSelected(
                this.date.startDate,
                this.date.endDate,
                this.compareDate.startDate,
                this.compareDate.endDate,
            );
        } else {
            this.getPage();
        }
    }

    onPageSizeChangeLocal (pageSize: number) {
        this.pageSize = pageSize;
        this.page = 1;
        this.getPage();
    }

    updateDateRange () {
        if (this.date.startDate && this.date.endDate) {
            this.paramService.setParams({
                from: moment(this.date.startDate),
                to: moment(this.date.endDate),
            });
        }
        this.exports = this.getResetExports();
        this.getPage();
    }

    setExportCategoryRef (type) {
        return this.statisticsExportBuilderService.buildCategoryRef(
            this.date.startDate,
            this.date.endDate,
            'YYYY-MM-DD',
            type,
        );
    }

    updateCompareDate (e: any): Promise<string> | void {
        const isFutureDates = this.statisticsDateComparisonService.isFutureDate(e.startDate) ||
            this.statisticsDateComparisonService.isFutureDate(e.endDate);

        if (isFutureDates) {
            return this.monModalService.alert(
                this.translateService.getString(
                    'The start and end date cannot be in the future, please enter the compare dates again',
                ),
            );
        }

        this.comparisonEnabled = e.startDate && e.endDate;

        if (this.comparisonEnabled) {
            this.compareDate.startDate = e.startDate;
            this.compareDate.endDate = e.endDate;
            this.pageSize = 10;
            this.page = 1;
            this.promise = this.onComparisonRangeSelected(
                this.date.startDate,
                this.date.endDate,
                this.compareDate.startDate,
                this.compareDate.endDate,
            );
        }
    }

    getResetExports () {
        return this.exports.map(exp => {
            return {
                ...exp,
                data: {
                    ...exp.data,
                    category_ref: this.setExportCategoryRef(exp.type || 'all'),
                },
            };
        });
    }
}
